/**
 * Undo (PLAN §6.2: "Per-user inverse patches (old values captured when the
 * patch is made). An undo is just a new patch").
 *
 * The inverse is computed by the writer when it makes the patch, against the
 * row as that writer saw it, and stored in the writer's own undo stack. Undoing
 * sends it under a NEW HLC, so it is an ordinary write: it wins over the
 * original, and it loses to anything written after it — including a later
 * edit by someone else, which is the per-user semantics PLAN asks for.
 */
import { defaultValueFor } from './fields.js';
import { compareHlc } from './hlc.js';
import type { JsonValue } from './primitives.js';
import type { Row, RowPatch } from './row.js';
import { inverseTextUpdate } from './text.js';

export type InverseMeta = {
  /** A fresh HLC from the undoing writer's clock. */
  readonly hlc: string;
  readonly origin: string;
  readonly clientOpId: string;
  /** The undoing writer's Yjs client id (restored characters are its inserts). */
  readonly textClientId: number;
};

export type InverseResult = { readonly ok: true; readonly patch: RowPatch } | { readonly ok: false; readonly error: string };

/**
 * The patch that restores what `patch` changed on `rowBefore`.
 *
 *   - a creation (`rowBefore` null) inverts to a soft delete;
 *   - a field inverts to its old value, or — if it had never been written —
 *     to the value it read as (`defaultValueFor`), since LWW has no unset;
 *   - a text update inverts through Yjs (`inverseTextUpdate`), removing only
 *     what it inserted, so others' concurrent typing survives.
 */
export function inversePatch(rowBefore: Row | null, patch: RowPatch, meta: InverseMeta): InverseResult {
  const envelope = { rowId: patch.rowId, hlc: meta.hlc, origin: meta.origin, clientOpId: meta.clientOpId };
  if (rowBefore === null) return { ok: true, patch: { ...envelope, set: { deleted: true } } };

  const set: Record<string, JsonValue> = {};
  for (const path of Object.keys(patch.set)) {
    const old = Object.hasOwn(rowBefore.f, path) ? rowBefore.f[path] : undefined;
    set[path] = old ?? defaultValueFor(rowBefore.kind, path);
  }
  const text: Record<string, string> = {};
  for (const [path, update] of Object.entries(patch.text ?? {})) {
    const before = Object.hasOwn(rowBefore.text, path) ? (rowBefore.text[path] ?? null) : null;
    const inverse = inverseTextUpdate(before, update, meta.textClientId);
    if (inverse === null) return { ok: false, error: `cannot invert the text update to ${path}` };
    text[path] = inverse;
  }
  return { ok: true, patch: Object.keys(text).length > 0 ? { ...envelope, set, text } : { ...envelope, set } };
}

/**
 * What of a patch is still ours to undo, given the row now: a field someone
 * wrote after us (its HLC is newer than the patch's) is left alone — undoing
 * it would silently overwrite their edit (PLAN §6.2: per-user undo). Text
 * edits always stay: their Yjs inverse removes only what this patch inserted.
 * A creation always stays (its inverse is a soft delete). Returns the fields
 * dropped too, so the caller can say so. Shared by `acp undo` and the SPA's undo stack,
 * so the two cannot drift.
 *
 * `supersededBy`: which write a field's current HLC stands for. A multi-step
 * undo writes the field itself between two undos of it (rename A→B→C, undo,
 * undo): the first undo's write RESTORES the state the undone patch was made
 * on, so for the next undo it counts as that earlier write, not a newer one.
 * The caller maps an HLC of one of its own undo writes to the HLC it restored
 * (null: the field had never been written), transitively; any other HLC —
 * someone else's edit, or the caller's own non-undoable write — maps to
 * itself. Null: no such writes (the CLI's single slot).
 */
export function undoableFields(
  patch: RowPatch,
  current: Row | null,
  supersededBy: ((path: string, hlc: string) => string | null) | null,
): { patch: RowPatch; overwritten: string[] } {
  if (patch.create !== undefined || current === null) return { patch, overwritten: [] };
  const set: Record<string, JsonValue> = {};
  const overwritten: string[] = [];
  for (const [path, value] of Object.entries(patch.set)) {
    const now = Object.hasOwn(current.h, path) ? current.h[path] : undefined;
    const effective = now === undefined ? null : supersededBy === null ? now : supersededBy(path, now);
    if (effective !== null && compareHlc(effective, patch.hlc) > 0) overwritten.push(path);
    else set[path] = value;
  }
  return { patch: { ...patch, set }, overwritten };
}
